import { Avatar, Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { Agent } from "@/api/generated";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";
import { buildAgentName } from "@/utilities/agentName";

import MatchingCompletedCharacterImage from "@assets/image/matching_completed_character.png";
import NoImage from "@assets/image/NoImage.svg";

import { BottomSheet } from "../BottomSheet";
import { CustomButton } from "../Button";
import { PickerField } from "../selection/PickerField";
import { StyledModal } from "../StyledModal";

const SCHEDULE_PICKER_Z_INDEX = theme.zIndex.modal + 2;

const useStyles = makeStyles(() => ({
  content: {
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [theme.breakpoints.up("md")]: { padding: 32, gap: 24 },
  },
  head: { display: "flex", flexDirection: "column", gap: 8, textAlign: "center" },
  title: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, [theme.breakpoints.up("md")]: { fontSize: 20 } },
  description: { fontSize: 14, lineHeight: 1.5, color: theme.palette.text.secondary, whiteSpace: "pre-line" },
  note: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary },
  // --- 日程入力（Figma「施策_マッチ成立（日程入力）」2026-10-27）。見出しの代わりに「Matching!」のイラストで成立を伝える。
  // 2倍書き出し（400×240）の PNG を 200×120 で表示する
  illustration: { display: "block", width: 200, height: 120, alignSelf: "center" },
  scheduleHead: { display: "flex", flexDirection: "column", gap: 4 },
  scheduleDescription: { fontSize: 14, lineHeight: 1.5, color: theme.palette.text.primary, whiteSpace: "pre-line" },
  scheduleNote: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary, textAlign: "center" },
  form: { display: "flex", flexDirection: "column", gap: 24 },
  // 見出し文言を支援技術にだけ残す（見た目は Figma どおりイラストのみ）
  visuallyHidden: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  schedule: { display: "flex", flexDirection: "column", gap: 12 },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: 8,
    "& > div": { marginBottom: 0, minWidth: 0 },
    "& > div > span": { fontSize: 12, fontWeight: 700 },
    "& button": { padding: "0 12px" },
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: "100%",
    alignItems: "center",
    "& button": { width: "100%", [theme.breakpoints.up("md")]: { width: 360, maxWidth: "100%" } },
  },
  recommendations: { display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 },
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: 8,
    [theme.breakpoints.up("md")]: { gap: 16 },
  },
  card: {
    backgroundColor: theme.palette.semantic["surface/base"],
    borderRadius: 8,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  avatar: { width: 48, height: 48 },
  company: {
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: "center",
    minHeight: 36,
    [theme.breakpoints.up("md")]: { fontSize: 14, minHeight: 42 },
    color: theme.palette.text.secondary,
    overflowWrap: "anywhere",
  },
  name: {
    fontSize: 12,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
    fontWeight: 700,
    lineHeight: 1.3,
    textAlign: "center",
    overflowWrap: "anywhere",
  },
}));

type Option = { value: string; label: string };
// 日程入力の隠し見出しの id。dialog の aria-labelledby から参照する（同時に開くのは 1 つだけ）
const SCHEDULE_TITLE_ID = "planner-approval-schedule-title";
interface Props {
  title: string;
  description: string;
  recommendationOnly: boolean;
  additionalAgents: Agent[];
  onClickAdditionalAgent?: (agent: Agent) => void;
  onClose: () => void;
  onComplete: () => void;
  onSkip: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  showSchedule: boolean;
  dateOptions: Option[];
  firstDate: string;
  secondDate: string;
  firstSlot: string;
  secondSlot: string;
  firstSlotOptions: Option[];
  secondSlotOptions: Option[];
  onFirstDate: (value: string) => void;
  onSecondDate: (value: string) => void;
  onFirstSlot: (value: string) => void;
  onSecondSlot: (value: string) => void;
}

/** 表示だけを担当。候補の日付・時間枠・保存・進行はApproveModalの既存処理を使う。 */
export const PlannerApprovalPresentation: React.FC<Props> = (props) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const recommendations = (
    <Box className={classes.recommendations}>
      <Box className={classes.list}>
        {props.additionalAgents.slice(0, 3).map((agent) => (
          <Box key={agent.id} className={classes.card}>
            <Avatar
              className={classes.avatar}
              src={agent.agent_profile_image?.image_url || NoImage}
              alt={buildAgentName(agent)}
            />
            <Typography className={classes.company}>{agent.company?.name || "-"}</Typography>
            <Typography className={classes.name}>{buildAgentName(agent)}</Typography>
            <CustomButton tone="primary" buttonSize="S" fullWidth onClick={() => props.onClickAdditionalAgent?.(agent)}>
              詳細
            </CustomButton>
          </Box>
        ))}
      </Box>
      <Typography className={classes.note}>
        満足度の高い不動産投資を行っている方の多くは、2〜3社から話を聞き、提案内容を比較した上で判断しています。（平均2.4人）
      </Typography>
    </Box>
  );
  const recommendationTitle = "あなたにオススメの認定プランナー";
  const recommendationDescription =
    "この認定プランナーに話を聞いた方の多くが、以下の認定プランナーにも話を聞いています。";
  const closeButton = (
    <CustomButton tone="text" buttonSize="M" fullWidth onClick={props.onClose}>
      閉じる
    </CustomButton>
  );
  if (props.recommendationOnly)
    return isMobile ? (
      <BottomSheet
        open
        title={recommendationTitle}
        description={<span className={classes.description}>{recommendationDescription}</span>}
        onOpen={() => undefined}
        onClose={props.onClose}
        footer={closeButton}
      >
        {recommendations}
      </BottomSheet>
    ) : (
      <StyledModal lockScroll role="dialog" ariaLabel={recommendationTitle} onClickOutside={props.onClose}>
        <Box className={classes.content}>
          <Box className={classes.head}>
            <Typography component="h2" className={classes.title}>
              あなたにオススメの
              <br />
              認定プランナー
            </Typography>
            <Typography className={classes.description}>{recommendationDescription}</Typography>
          </Box>
          {recommendations}
          {closeButton}
        </Box>
      </StyledModal>
    );
  return (
    <StyledModal
      lockScroll
      role="dialog"
      // 日程入力は見出しをイラストに置き換えるため、隠した h2 を名前にする（aria-label と二重にしない）
      ariaLabel={props.showSchedule ? undefined : props.title}
      labelledBy={props.showSchedule ? SCHEDULE_TITLE_ID : undefined}
      onClickOutside={props.isSubmitting ? undefined : props.onClose}
    >
      <Box className={classes.content}>
        {props.showSchedule ? (
          <>
            {/* 見出し「マッチングが成立しました！」は視覚的に隠して読み上げだけに残す。
                dialog はフォーカスを移さない（StyledModal の disableAutoFocus）ため、aria-label だけでは読まれない恐れがある。
                イラストは装飾扱い（alt は空） */}
            <Typography component="h2" id={SCHEDULE_TITLE_ID} className={classes.visuallyHidden}>
              {props.title}
            </Typography>
            <img src={MatchingCompletedCharacterImage} alt="" className={classes.illustration} />
            <Box className={classes.scheduleHead}>
              <Typography className={classes.scheduleDescription}>{props.description}</Typography>
              <Typography className={classes.scheduleNote}>※1週間以内の日付を指定してください</Typography>
            </Box>
            <Box className={classes.form}>
              <Box className={classes.schedule}>
                <Box className={classes.row} role="group" aria-label="第1希望">
                  <PickerField
                    label="第1希望日"
                    value={props.firstDate}
                    options={props.dateOptions}
                    onChange={(v) => props.onFirstDate(String(v))}
                    sheetZIndex={SCHEDULE_PICKER_Z_INDEX}
                  />
                  <PickerField
                    label="時間帯"
                    value={props.firstSlot}
                    options={props.firstSlotOptions}
                    onChange={(v) => props.onFirstSlot(String(v))}
                    sheetZIndex={SCHEDULE_PICKER_Z_INDEX}
                  />
                </Box>
                <Box className={classes.row} role="group" aria-label="第2希望">
                  <PickerField
                    label="第2希望日"
                    value={props.secondDate}
                    options={props.dateOptions}
                    onChange={(v) => props.onSecondDate(String(v))}
                    sheetZIndex={SCHEDULE_PICKER_Z_INDEX}
                  />
                  <PickerField
                    label="時間帯"
                    value={props.secondSlot}
                    options={props.secondSlotOptions}
                    onChange={(v) => props.onSecondSlot(String(v))}
                    sheetZIndex={SCHEDULE_PICKER_Z_INDEX}
                  />
                </Box>
              </Box>
              <Box className={classes.actions}>
                <CustomButton
                  tone="trust"
                  buttonSize="L"
                  fullWidth
                  disabled={props.isSubmitting || !props.canSubmit}
                  onClick={props.onSubmit}
                >
                  送信して完了
                </CustomButton>
                <CustomButton tone="text" buttonSize="M" fullWidth disabled={props.isSubmitting} onClick={props.onSkip}>
                  スキップ
                </CustomButton>
              </Box>
            </Box>
            <Typography className={classes.note}>
              ご入力の日程をもとに、認定プランナーよりご連絡いたします。なお、連絡先は「認定プランナー情報」からもご確認いただけます。
            </Typography>
          </>
        ) : (
          <>
            {/* 対象条件外（承諾待ち）の案内。Figma に無いため見出し＋説明＋完了の従来レイアウトのまま */}
            <Box className={classes.head}>
              <Typography component="h2" className={classes.title}>
                {props.title}
              </Typography>
              <Typography className={classes.description}>{props.description}</Typography>
            </Box>
            <Box className={classes.actions}>
              <CustomButton tone="primary" buttonSize="L" fullWidth onClick={props.onComplete}>
                完了
              </CustomButton>
            </Box>
          </>
        )}
      </Box>
    </StyledModal>
  );
};
